import type { Plan as PlanType } from '@wix/ambassador-appmarket-v1-app-plans/types';
import type { IAppPlan, MeterdBilling, Discount, PlanPrice } from './types';
import { CycleType, DurationUnit } from './types';

/**
 *
 * Represents a facade for Plan type on the app-plans service.
 *
 * @class AppPlan
 */

export class AppPlan {
  constructor(
    private readonly plan: PlanType,
    private readonly showPriceWithTax: boolean,
    private readonly currencySymbol: string,
  ) {}

  get vendorId(): string {
    return this.plan.vendorId ?? '';
  }

  get meterdBilling(): MeterdBilling | undefined {
    let meterdBilling;
    const meterdPrice = this.plan?.prices?.find(
      (price) => !!price.usageBaseOptions,
    );

    const baseFee = meterdPrice?.usageBaseOptions?.monthlyBaseFee ?? '0.00';
    const minimumCharge =
      meterdPrice?.usageBaseOptions?.minimumChargeIncrement ?? '0.00';
    const additionalCharge =
      meterdPrice?.usageBaseOptions?.customChargeDescription ?? '';

    const formattedMinimumCharge = `${this.currencySymbol}${minimumCharge}`;
    let outline = additionalCharge;

    if (/\{[^{}]+\}/.test(additionalCharge)) {
      outline = additionalCharge.replace(/\{[^{}]+\}/g, formattedMinimumCharge);
    } else {
      const symbolIndex = additionalCharge.indexOf(minimumCharge);
      if (symbolIndex !== -1) {
        outline =
          additionalCharge.slice(0, symbolIndex) +
          this.currencySymbol +
          additionalCharge.slice(symbolIndex);
      }
    }

    if (meterdPrice?.usageBaseOptions) {
      meterdBilling = {
        outline,
        baseFee,
        discountBaseFee: this.showPriceWithTax
          ? meterdPrice.discount?.totalPrice
          : meterdPrice.discount?.priceBeforeTax,
      };
    }
    return meterdBilling;
  }

  get yearlyPrice(): PlanPrice | undefined {
    const price = this.plan?.prices?.find(
      (_price) =>
        _price.billingCycle?.cycleDuration?.unit === DurationUnit.YEAR &&
        _price.billingCycle?.cycleType === CycleType.RECURRING,
    );
    return {
      price: this.showPriceWithTax ? price?.totalPrice : price?.priceBeforeTax,
      discountPrice: this.showPriceWithTax
        ? price?.discount?.totalPrice
        : price?.discount?.priceBeforeTax,
    };
  }

  get monthlyPrice(): PlanPrice | undefined {
    const price = this.plan?.prices?.find(
      (_price) =>
        _price.billingCycle?.cycleDuration?.unit === DurationUnit.MONTH &&
        _price.billingCycle?.cycleType === CycleType.RECURRING,
    );
    return {
      price: this.showPriceWithTax ? price?.totalPrice : price?.priceBeforeTax,
      discountPrice: this.showPriceWithTax
        ? price?.discount?.totalPrice
        : price?.discount?.priceBeforeTax,
    };
  }

  get oneTimePrice(): PlanPrice | undefined {
    const price = this.plan?.prices?.find(
      (_price) => _price.billingCycle?.cycleType === CycleType.ONE_TIME,
    );
    return {
      price: this.showPriceWithTax ? price?.totalPrice : price?.priceBeforeTax,
      discountPrice: this.showPriceWithTax
        ? price?.discount?.totalPrice
        : price?.discount?.priceBeforeTax,
    };
  }

  /**
   *  There are 3 types of discounts: wixSale, devSale and App Benefit.
   *  For the time being, app with benefit cannot have a sale discount.
   */

  get discount(): Discount | undefined {
    const discount = this.plan?.prices?.find((price) =>
      Boolean(price.discount?.sourceType),
    )?.discount;
    if (!discount) {
      return undefined;
    }
    const { amount, type, sourceType, saleOptions } = discount;
    if (amount && type && sourceType) {
      const planDiscount: Discount = {
        sourceType,
        saleType: saleOptions?.saleType,
        type,
        amount,
        endDate: saleOptions?.endDate || new Date(),
      };
      return planDiscount;
    }
    return undefined;
  }

  get billingSource() {
    return this.plan?.prices?.find((price) => price.billingSource)
      ?.billingSource;
  }

  toJSON(): IAppPlan {
    return {
      meterdBilling: this.meterdBilling,
      yearlyPrice: this.yearlyPrice,
      monthlyPrice: this.monthlyPrice,
      oneTimePrice: this.oneTimePrice,
      vendorId: this.vendorId,
      discount: this.discount,
      billingSource: this.billingSource,
    };
  }
}
